import React from 'react';
import {
    List, Datagrid, Pagination, TextField, EmailField, BooleanField, DateField, SearchInput,
    SingleFieldList, ChipField, ArrayField, downloadCSV, FunctionField, useNotify, useRefresh
} from 'react-admin';
import Button from '@material-ui/core/Button';
import * as Constants from '../constants';

import { create } from 'jss';
import rtl from 'jss-rtl';
import JssProvider from 'react-jss/lib/JssProvider';
import { jssPreset } from '@material-ui/core/styles';
import jsonExport from 'jsonexport/dist';

const UserPagination = props => <Pagination rowsPerPageOptions={[100, 50, 25, 10]} {...props} />;

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

const exporter = async (records) => {
    const data = records.map(record => {
        let tracks =record.activities.length > 0 ? `${record.activities[0].track_title}` : "";
        let codes =record.activities.length > 0 ? `${record.activities[0].code_value}` : "";
        for(let i=1;i<record.activities.length;i++){
            tracks = `${tracks}, ${record.activities[i].track_title}`;
            codes = `${codes}, ${record.activities[i].code_value}`;
        }
        return({
        id: record.id,
        full_name: record.full_name,
        email: record.email,
        phone: record.phone,
        last_login: record.last_login,
        date_joined: record.date_joined,
        is_staff: record.is_staff,
        tracks: tracks,
        codes: codes
    })});
    jsonExport(data, {
        headers: ['id', 'full_name', 'email', 'phone', 'last_login', 'date_joined', 'is_staff', "tracks", "codes"] // order fields in the export
    }, (err, csv) => {
        var BOM = String.fromCharCode(0xFEFF);
        downloadCSV(BOM + csv, 'Users'); // download as 'Users.csv` file
    })
};

const UserFilter = [
        <SearchInput source="q" alwaysOn />
];

const AdminToggleButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [isSaving, setIsSaving] = React.useState(false);

    if (!record) {
        return null;
    }

    const nextIsAdmin = !record.is_staff;
    const buttonLabel = nextIsAdmin ? 'Make Admin' : 'Remove Admin';
    const confirmAction = nextIsAdmin ? 'grant admin permissions to' : 'remove admin permissions from';
    const currentUserId = Number(localStorage.getItem('user_id'));
    const isCurrentUser = Number(record.id) === currentUserId;

    const getErrorMessage = (error) => {
        const body = error?.body || error?.json || error?.data;
        if (body?.detail) {
            return body.detail;
        }
        if (typeof body === 'string') {
            return body;
        }
        if (error?.message) {
            return error.message;
        }
        return 'Failed to update admin permissions';
    };

    const onToggleAdmin = (event) => {
        event.stopPropagation();
        const shouldContinue = window.confirm(`Are you sure you want to ${confirmAction} ${record.full_name || record.email}?`);
        if (!shouldContinue) {
            return;
        }

        setIsSaving(true);
        const token = localStorage.getItem('token');
        fetch(`${Constants.siteUrl}/users/${record.id}/set_admin/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ is_staff: nextIsAdmin }),
        })
            .then(async (response) => {
                const body = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const err = new Error(body?.detail || 'Failed to update admin permissions');
                    err.body = body;
                    throw err;
                }
                return body;
            })
            .then(() => {
                notify(`Admin permissions updated for ${record.full_name || record.email}`, 'info');
                refresh();
            })
            .catch((error) => {
                notify(getErrorMessage(error), 'warning');
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    return (
        <Button
            size="small"
            color={nextIsAdmin ? 'primary' : 'secondary'}
            variant="outlined"
            onClick={onToggleAdmin}
            disabled={isSaving || (isCurrentUser && !nextIsAdmin)}
        >
            {isSaving ? 'Saving...' : buttonLabel}
        </Button>
    );
};

export const UserList = props => (
    <JssProvider jss={jss}>
        <List {...props} exporter={exporter} pagination={<UserPagination />} filters={UserFilter}>
            <Datagrid>
                <TextField source="id" />
                <TextField source="full_name" label={"Full Name"} />
                <EmailField source="email" label={"Email"} />
                <TextField source="phone" label={"Phone"} />
                <DateField showTime source="last_login" label={"Last Login"} />
                <DateField showTime source="date_joined" label={"Date Joined"} />
                <ArrayField label="Courses" source="activities" allowEmpty perPage={1000}>
                    <SingleFieldList>
                        <ChipField source="track_title" />
                    </SingleFieldList>
                </ArrayField >
                <ArrayField label="Codes" source="activities" allowEmpty perPage={1000}>
                    <SingleFieldList>
                        <ChipField source="code_value" />
                    </SingleFieldList>
                </ArrayField >
                <BooleanField source="is_staff" label={"Is Admin"} />
                <FunctionField
                    label="Admin Action"
                    render={record => <AdminToggleButton record={record} />}
                />
            </Datagrid>
        </List>
    </JssProvider>
);